import { useCallback, useEffect, useMemo, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import {
  MOBILE_XLSX_EXPORT_READY_EVENT,
  type MobileXlsxExportReadyDetail,
  useTranslation,
} from '@spcc/core'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@spcc/uikit'

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function normalizeShareUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) {
    return uri
  }
  if (uri.startsWith('/')) return `file://${uri}`
  return uri
}

function isFileShareUri(uri: string): boolean {
  return uri.startsWith('file://')
}

function getNativeShareOptions(
  fileUri: string,
  fileName: string
): { title: string; files?: string[]; url?: string } {
  const normalized = normalizeShareUri(fileUri)
  const platform = typeof Capacitor?.getPlatform === 'function' ? Capacitor.getPlatform() : 'web'

  if (platform === 'android' && isFileShareUri(normalized)) {
    return { title: fileName, files: [normalized] }
  }

  return { title: fileName, url: normalized }
}

function isNativePlatform(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

function getTableRows(detail: MobileXlsxExportReadyDetail): Array<Array<string>> {
  return detail.preview.rows.map((row) =>
    row.map((value) => (value === null ? '' : String(value)))
  )
}

/**
 * Listens for {@link MOBILE_XLSX_EXPORT_READY_EVENT} from native `exportToXlsx` and shows
 * a preview dialog with Share. Mount once under `I18nProvider` (e.g. portal root layout).
 */
export function MobileExportPreviewDrawer() {
  const { t } = useTranslation()
  const [exportDetail, setExportDetail] = useState<MobileXlsxExportReadyDetail | null>(null)
  const [sharing, setSharing] = useState(false)

  useEffect(() => {
    const handleExportReady = (event: Event) => {
      const customEvent = event as CustomEvent<MobileXlsxExportReadyDetail>
      setExportDetail(customEvent.detail)
    }

    window.addEventListener(MOBILE_XLSX_EXPORT_READY_EVENT, handleExportReady as EventListener)
    return () => {
      window.removeEventListener(MOBILE_XLSX_EXPORT_READY_EVENT, handleExportReady as EventListener)
    }
  }, [])

  const closeDrawer = useCallback(() => {
    setExportDetail(null)
  }, [])

  const tableRows = useMemo(() => (exportDetail ? getTableRows(exportDetail) : []), [exportDetail])

  const handleShare = useCallback(async () => {
    if (!exportDetail) return
    setSharing(true)

    try {
      if (isNativePlatform()) {
        await Share.share(getNativeShareOptions(exportDetail.fileUri, exportDetail.fileName))
      } else {
        const file = new File([], exportDetail.fileName)
        if (typeof navigator.share === 'function') {
          await navigator.share({ title: exportDetail.fileName, files: [file] })
        } else {
          throw new Error('Share not supported')
        }
      }
    } catch (error) {
      console.error('[MobileExportPreviewDrawer] Share failed', error)
      toast({
        title: t('common.error'),
        description: t('common.shareFileFailed'),
        variant: 'destructive',
      })
    } finally {
      setSharing(false)
    }
  }, [exportDetail, t])

  return (
    <Dialog open={!!exportDetail} onOpenChange={(open) => { if (!open) closeDrawer() }}>
      <DialogContent className="w-[95vw] max-w-4xl h-[85vh] p-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b bg-gradient-to-r from-slate-900 to-slate-700 text-white">
          <DialogTitle>{t('common.export')}</DialogTitle>
          <DialogDescription className="text-slate-200">
            {exportDetail?.sheetName ?? t('common.export')}
          </DialogDescription>
          <div className="flex items-center gap-3 text-slate-100 text-sm">
            <span className="font-semibold">{exportDetail?.fileName}</span>
            <Badge variant="secondary" className="bg-slate-200/20 text-slate-100 border-slate-100/30">
              XLSX
            </Badge>
            <span>{formatFileSize(exportDetail?.fileSizeBytes ?? 0)}</span>
          </div>
        </DialogHeader>

        <div className="px-4 sm:px-6 py-4 overflow-auto h-full bg-slate-50">
          <div className="min-w-[680px]">
            <table className="w-full text-sm bg-white border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  {(exportDetail?.preview.headers ?? []).map((header) => (
                    <th key={header} className="px-3 py-2 text-start font-semibold text-slate-700 border-b border-slate-200">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`} className="odd:bg-white even:bg-slate-50">
                    {row.map((cell, cellIndex) => (
                      <td key={`cell-${rowIndex}-${cellIndex}`} className="px-3 py-2 border-b border-slate-100 text-slate-700">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 py-4 border-t bg-white">
          <Button variant="outline" onClick={closeDrawer}>{t('common.close')}</Button>
          <Button onClick={() => { void handleShare() }} disabled={sharing}>
            {sharing ? t('common.sharing') : t('common.share')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
